import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import { useLocale } from '../../context/LocaleContext';

const COPY = {
  en: {
    heading: 'About Me',
    p1: "Hey there! I'm Jaime, a software engineer with a knack for building cool stuff. I'm passionate about tech and its power to solve problems and make life a little easier (or way more fun!).",
    p2Before: 'On my journey so far, I’ve had the privilege of working at',
    p2Link: 'a Caterpillar distributor',
    p2After: ', and a start-up.',
    p3: 'Here are a few technologies I’ve been working with:',
    imgAlt: 'Headshot',
  },
  es: {
    heading: 'Sobre Mí',
    p1: 'Soy Jaime, ingeniero de software con talento para construir cosas geniales. Me apasiona la tecnología y su poder para resolver problemas y hacer la vida un poco más fácil (¡o mucho más divertida!).',
    p2Before: 'En mi trayectoria he tenido el privilegio de trabajar en',
    p2Link: 'un distribuidor de Caterpillar',
    p2After: ', y en una startup.',
    p3: 'Estas son algunas de las tecnologías con las que he trabajado:',
    imgAlt: 'Foto de perfil',
  },
};

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const locale = useLocale();
  const copy = COPY[locale] || COPY.en;

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = ['JavaScript (ES6+)', 'Flutter', 'React', 'Node.js', 'Python', 'WordPress'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">{copy.heading}</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>{copy.p1}</p>

            <p>
              {copy.p2Before} <a href="https://iasaglobal.com">{copy.p2Link}</a>
              {copy.p2After}
            </p>

            <p>{copy.p3}</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.png"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt={copy.imgAlt}
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
